import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import facilityService from '../services/facilityService';
import bookingService from '../services/bookingService';
import './BookingsImportPage.css';

const BookingsImportPage = () => {
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    loadFacilities();
  }, []);

  const loadFacilities = async () => {
    try {
      const data = await facilityService.getAllFacilities();
      setFacilities(data.filter(f => f.active));
    } catch (error) {
      setError('Fehler beim Laden der Anlagen');
      console.error('Error loading facilities:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFacility || !selectedDate || !selectedFile) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    setLoading(true);
    setError('');
    setImportResult(null);

    try {
      const result = await bookingService.importBookings(
        selectedFile,
        selectedFacility,
        selectedDate
      );
      
      setImportResult(result);
      
      if (result.failed_count === 0) {
        setTimeout(() => {
          navigate('/bookings');
        }, 3000);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim Import der Buchungen');
      console.error('Import error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setError('');
    if (document.getElementById('file-input')) {
      document.getElementById('file-input').value = '';
    }
  };

  return (
    <div className="bookings-import-page">
      <div className="page-header">
        <h1>Buchungen importieren</h1>
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          Zurück zum Dashboard
        </button>
      </div>

      <div className="import-container">
        {!importResult ? (
          <form onSubmit={handleSubmit} className="import-form">
            <div className="form-group">
              <label htmlFor="facility">Anlage *</label>
              <select
                id="facility"
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                required
              >
                <option value="">Bitte wählen...</option>
                {facilities.map(facility => (
                  <option key={facility.id} value={facility.id}>
                    {facility.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="date">Datum *</label>
              <input
                type="date"
                id="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="file-input">Excel-Datei *</label>
              <input
                type="file"
                id="file-input"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                required
              />
              {selectedFile && (
                <div className="file-info">
                  Ausgewählte Datei: {selectedFile.name}
                </div>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Importiere...' : 'Import starten'}
              </button>
              <button type="button" onClick={handleReset} className="btn btn-secondary">
                Zurücksetzen
              </button>
            </div>
          </form>
        ) : (
          <div className="import-result">
            <h2>Import abgeschlossen</h2>
            
            <div className="result-summary">
              <div className="result-item success">
                <span className="result-label">Erfolgreich importiert:</span>
                <span className="result-value">{importResult.imported_count}</span>
              </div>
              
              {importResult.failed_count > 0 && (
                <div className="result-item error">
                  <span className="result-label">Fehlgeschlagen:</span>
                  <span className="result-value">{importResult.failed_count}</span>
                </div>
              )}
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="error-details">
                <h3>Fehlerdetails:</h3>
                <ul>
                  {importResult.errors.map((error, index) => (
                    <li key={index}>
                      Zeile {error.row}: {error.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="result-actions">
              <button onClick={handleReset} className="btn btn-primary">
                Weitere Datei importieren
              </button>
              <button onClick={() => navigate('/bookings')} className="btn btn-secondary">
                Zu den Buchungen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingsImportPage;